import { derived, readable, type Readable, type Writable } from 'svelte/store';

import { TeoCore } from '@/teoCore/TeoCore';
import type { TeoText, TextState } from '@/teoCore/text/TeoText';
import type { TeoView } from '@/teoCore/text/TextUi';
import { useReactiveState } from '@/utils/reactive-store/svelte';

export function useActiveTextReadable(): Readable<TeoText> {
  return readable(TeoCore.instance.activeText, (set) => {
    return TeoCore.instance.events.addEventListener('activeTextChanged', () => {
      set(TeoCore.instance.activeText);
    });
  });
}

function getActiveTextStateReadable() {
  return derived(useActiveTextReadable(), (activeText) => activeText.state);
}

export function useActiveTextState(): Readable<TextState>;
export function useActiveTextState<TValue>(lens: {
  get: (state: TextState) => TValue;
}): Readable<TValue>;
export function useActiveTextState<TValue>(lens: {
  get: (state: TextState) => TValue;
  set: (state: TextState, value: TValue) => void;
}): Writable<TValue>;
export function useActiveTextState<TValue>(
  lens?:
    | {
        get: (state: TextState) => TValue;
        set?: (state: TextState, value: TValue) => void;
      }
    | undefined,
) {
  if (!lens) {
    return useReactiveState(getActiveTextStateReadable());
  } else {
    if (lens.set) {
      return useReactiveState(getActiveTextStateReadable(), {
        get: lens.get,
        set: lens.set,
      });
    } else {
      return useReactiveState(getActiveTextStateReadable(), { get: lens.get });
    }
  }
}

export function useTextState(teoText: TeoText): Readable<TextState>;
export function useTextState<TValue>(
  teoText: TeoText,
  lens: { get: (state: TextState) => TValue },
): Readable<TValue>;
export function useTextState<TValue>(
  teoText: TeoText,
  lens: {
    get: (state: TextState) => TValue;
    set: (state: TextState, value: TValue) => void;
  },
): Writable<TValue>;
export function useTextState<TValue>(
  teoText: TeoText,
  lens?:
    | {
        get: (state: TextState) => TValue;
        set?: (state: TextState, value: TValue) => void;
      }
    | undefined,
) {
  if (!lens) {
    return useReactiveState(teoText.state);
  }

  if (lens.set) {
    return useReactiveState(teoText.state, {
      get: lens.get,
      set: lens.set,
    });
  } else {
    return useReactiveState(teoText.state, { get: lens.get });
  }
}

export function useViewType(teoText?: TeoText) {
  const lens = {
    get: (state: TextState) => state.view,
    set: (state: TextState, value: TeoView) => {
      state.view = value;
    },
  } as const;
  return (teoText ? useTextState(teoText, lens) : useActiveTextState(lens)) as Writable<TeoView>;
}

export function useHasViewType(view: TeoView | TeoView[], teoText?: TeoText) {
  const lens = {
    get: (state: TextState) =>
      Array.isArray(view) ? view.includes(state.view) : view === state.view,
  };
  return teoText ? useTextState(teoText, lens) : useActiveTextState(lens);
}

// --- Deprecated ---

export const useHasTextViewType = useHasViewType;
